export default {
    data() {
        const boot = typeof window !== 'undefined' ? window.vAiAnswers || null : null;
        return {
            aiAnswersBoot: boot,
            aiAnswersDrawerOpen: false,
            aiAnswersOpenAfterHelpPending: false,
            aiAnswersPreviousFocus: null,
            aiAnswersPreviousOverflow: '',
            aiAnswersDocumentKeydown: null,
            aiAnswersConversations: [],
            aiAnswersConversationId: null,
            aiAnswersMessages: [],
            aiAnswersQuestion: '',
            aiAnswersBusy: false,
            aiAnswersLoading: false,
            aiAnswersError: '',
            aiAnswersReviewMode: !!boot?.reviewOnly,
            aiAnswersShowConversations: false,
            aiAnswersReviewRows: [],
            aiAnswersReviewNextBefore: null,
            aiAnswersPollTimer: null,
            aiAnswersPollingStartedAt: null,
            aiAnswersRequestVersion: 0,
            aiAnswersRefreshVersion: 0,
            aiAnswersPendingRequestId: null,
            aiAnswersPendingQuestion: null,
        };
    },
    computed: {
        aiAnswersAssistantName() {
            return this.aiAnswersBoot?.staff ? 'PonoAI' : (this.aiAnswersBoot?.assistantName || 'PonoAI');
        },
        aiAnswersDisplayMessages() {
            if (!this.aiAnswersBusy || !this.aiAnswersPendingQuestion || this.aiAnswersPendingAnswer) return this.aiAnswersMessages;
            return [...this.aiAnswersMessages,
                { id: 'sending-question', role: 'user', body: this.aiAnswersPendingQuestion, status: 'complete' },
                { id: 'sending-answer', role: 'assistant', body: '', status: 'processing' },
            ];
        },
        aiAnswersPendingAnswer() {
            return !this.aiAnswersReviewMode && this.aiAnswersMessages.some(message =>
                message.role === 'assistant' && ['pending', 'processing'].includes(message.status));
        },
        aiAnswersWorking() {
            return this.aiAnswersBusy || this.aiAnswersPendingAnswer;
        },
        aiAnswersProgressLabel() {
            return 'Finding an answer…';
        },
    },
    mounted() {
        if (this.aiAnswersBoot) {
            this.aiAnswersDocumentKeydown = event => {
                if (!this.aiAnswersDrawerOpen) return;
                if (event.key === 'Escape') {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    this.aiAnswersCloseDrawer();
                } else this.aiAnswersKeydown(event);
            };
            document.addEventListener('keydown', this.aiAnswersDocumentKeydown, true);
        }
        if (this.aiAnswersBoot?.autoOpen) this.aiAnswersOpenDrawer();
    },
    beforeUnmount() {
        if (this.aiAnswersDocumentKeydown) document.removeEventListener('keydown', this.aiAnswersDocumentKeydown, true);
        this.aiAnswersStopPolling();
        if (this.aiAnswersDrawerOpen) document.body.style.overflow = this.aiAnswersPreviousOverflow;
    },
    methods: {
        aiAnswersOpenFromHelp() {
            if (!this.aiAnswersBoot || this.aiAnswersDrawerOpen) return;
            if (this.help_drawer_open && typeof this.help_closeDrawer === 'function') {
                this.aiAnswersOpenAfterHelpPending = true;
                this.help_closeDrawer('ai-answers');
            } else this.aiAnswersOpenDrawer();
        },
        aiAnswersOpenAfterHelp() {
            if (!this.aiAnswersOpenAfterHelpPending) return;
            this.aiAnswersOpenAfterHelpPending = false;
            this.$nextTick(() => this.aiAnswersOpenDrawer(this.help_previous_focus));
        },
        async aiAnswersOpenDrawer(previousFocus = null) {
            if (!this.aiAnswersBoot || this.aiAnswersDrawerOpen) return;
            this.aiAnswersPreviousFocus = previousFocus?.isConnected ? previousFocus : document.activeElement;
            this.aiAnswersPreviousOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            this.aiAnswersDrawerOpen = true;
            this.aiAnswersError = '';
            this.$nextTick(() => this.aiAnswersFocusQuestion());
            if (this.aiAnswersBoot.reviewOnly) await this.aiAnswersLoadReview();
            else {
                await this.aiAnswersLoadList();
                if (this.aiAnswersConversationId) await this.aiAnswersRefresh();
            }
        },
        aiAnswersFocusQuestion() {
            if (!this.aiAnswersDrawerOpen) return;
            if (this.aiAnswersBoot?.staff && typeof this.admin2026FocusDrawerBySelector === 'function') {
                this.admin2026FocusDrawerBySelector('[data-ai-answers-drawer]', {
                    delay: 0, preferField: true, focusSelector: 'textarea[aria-label="Your question"]:not([disabled])',
                    onlyIfFocusOutside: false,
                });
            } else {
                const question = this.$refs.aiAnswersQuestionInput;
                const target = question && !question.disabled ? question : this.$refs.aiAnswersPanel;
                target?.focus({ preventScroll: true });
            }
        },
        aiAnswersCloseDrawer() {
            if (!this.aiAnswersDrawerOpen) return;
            this.aiAnswersStopPolling();
            this.aiAnswersRequestVersion++;
            this.aiAnswersDrawerOpen = false;
            this.aiAnswersLoading = false;
            document.body.style.overflow = this.aiAnswersPreviousOverflow;
            const previous = this.aiAnswersPreviousFocus;
            this.aiAnswersPreviousFocus = null;
            this.$nextTick(() => previous?.isConnected && previous.focus({ preventScroll: true }));
        },
        aiAnswersKeydown(event) {
            if (event.key !== 'Tab') return;
            const panel = this.$refs.aiAnswersPanel;
            if (!panel) return;
            const focusable = Array.from(panel.querySelectorAll('a[href], button, input, textarea, [tabindex="0"]'))
                .filter(element => !element.disabled && element.getClientRects().length);
            const first = focusable[0], last = focusable[focusable.length - 1];
            if (!first) { event.preventDefault(); panel.focus(); }
            else if (!panel.contains(document.activeElement) || document.activeElement === panel) {
                event.preventDefault(); (event.shiftKey ? last : first).focus();
            }
            else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
                event.preventDefault(); last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault(); first.focus();
            }
        },
        aiAnswersStopPolling(resetDelay = true) {
            if (this.aiAnswersPollTimer) clearTimeout(this.aiAnswersPollTimer);
            this.aiAnswersPollTimer = null;
            if (resetDelay) this.aiAnswersPollingStartedAt = null;
        },
        async aiAnswersRequest(url, options = {}) {
            const response = await fetch(url, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '' },
                ...options,
            });
            const body = await response.json();
            if (!response.ok || body.success === false) throw new Error(body.message || 'The request could not be completed.');
            return body;
        },
        async aiAnswersLoadList() {
            const version = this.aiAnswersRequestVersion;
            this.aiAnswersLoading = true;
            try {
                const body = await this.aiAnswersRequest(this.aiAnswersBoot.listUrl);
                if (version !== this.aiAnswersRequestVersion) return;
                this.aiAnswersConversations = body.conversations || [];
            } catch (error) {
                if (version === this.aiAnswersRequestVersion) this.aiAnswersError = error.message;
            } finally {
                if (version === this.aiAnswersRequestVersion) this.aiAnswersLoading = false;
            }
        },
        aiAnswersNewConversation() {
            if (this.aiAnswersWorking || this.aiAnswersBoot?.reviewOnly) return;
            this.aiAnswersStopPolling();
            this.aiAnswersRequestVersion++;
            this.aiAnswersReviewMode = false;
            this.aiAnswersShowConversations = false;
            this.aiAnswersConversationId = null;
            this.aiAnswersMessages = [];
            this.aiAnswersQuestion = '';
            this.aiAnswersError = '';
            this.aiAnswersLoading = false;
            this.aiAnswersPendingRequestId = null;
            this.aiAnswersPendingQuestion = null;
            this.$nextTick(() => this.aiAnswersFocusQuestion());
        },
        async aiAnswersOpen(id) {
            if (this.aiAnswersWorking) return;
            this.aiAnswersStopPolling();
            this.aiAnswersRequestVersion++;
            this.aiAnswersReviewMode = false;
            this.aiAnswersShowConversations = false;
            this.aiAnswersConversationId = id;
            this.aiAnswersPendingQuestion = null;
            this.aiAnswersPendingRequestId = null;
            this.aiAnswersError = '';
            const version = this.aiAnswersRequestVersion;
            this.aiAnswersLoading = true;
            try { await this.aiAnswersRefresh(); }
            finally {
                if (version === this.aiAnswersRequestVersion) this.aiAnswersLoading = false;
            }
        },
        async aiAnswersRefresh() {
            if (!this.aiAnswersDrawerOpen || !this.aiAnswersConversationId || this.aiAnswersReviewMode) return;
            this.aiAnswersStopPolling(false);
            const id = this.aiAnswersConversationId, version = this.aiAnswersRequestVersion;
            const refreshVersion = ++this.aiAnswersRefreshVersion;
            try {
                const body = await this.aiAnswersRequest(this.aiAnswersBoot.showBaseUrl + '/' + encodeURIComponent(id));
                if (version !== this.aiAnswersRequestVersion || refreshVersion !== this.aiAnswersRefreshVersion
                    || this.aiAnswersConversationId !== id || this.aiAnswersReviewMode) return;
                this.aiAnswersError = '';
                this.aiAnswersMessages = body.messages || [];
                this.$nextTick(() => {
                    const messages = this.$refs.aiAnswersMessages;
                    if (messages) messages.scrollTop = messages.scrollHeight;
                });
                if (this.aiAnswersPendingAnswer) {
                    if (this.aiAnswersPollingStartedAt === null) this.aiAnswersPollingStartedAt = Date.now();
                    const delay = Date.now() - this.aiAnswersPollingStartedAt < 15000 ? 500 : 2000;
                    this.aiAnswersPollTimer = setTimeout(() => this.aiAnswersRefresh(), delay);
                } else this.aiAnswersPollingStartedAt = null;
            } catch (error) {
                if (version === this.aiAnswersRequestVersion && refreshVersion === this.aiAnswersRefreshVersion
                    && this.aiAnswersConversationId === id) this.aiAnswersError = error.message;
            }
        },
        async aiAnswersAsk() {
            const question = this.aiAnswersQuestion.trim();
            if (!question || this.aiAnswersWorking || this.aiAnswersLoading || this.aiAnswersReviewMode || !this.aiAnswersBoot) return;
            this.aiAnswersBusy = true;
            this.aiAnswersError = '';
            if (this.aiAnswersPendingQuestion !== question) {
                this.aiAnswersPendingQuestion = question;
                this.aiAnswersPendingRequestId = crypto.randomUUID();
            }
            this.aiAnswersStopPolling();
            this.aiAnswersRefreshVersion++;
            this.$nextTick(() => {
                const messages = this.$refs.aiAnswersMessages;
                if (messages) messages.scrollTop = messages.scrollHeight;
            });
            try {
                const body = await this.aiAnswersRequest(this.aiAnswersBoot.askUrl, {
                    method: 'POST', body: JSON.stringify({ conversation_id: this.aiAnswersConversationId,
                        request_id: this.aiAnswersPendingRequestId, question }),
                });
                this.aiAnswersQuestion = '';
                this.aiAnswersConversationId = body.conversation_id;
                this.aiAnswersPendingQuestion = null;
                this.aiAnswersPendingRequestId = null;
                if (Array.isArray(body.messages)) {
                    this.aiAnswersMessages = body.messages;
                    this.$nextTick(() => {
                        const messages = this.$refs.aiAnswersMessages;
                        if (messages) messages.scrollTop = messages.scrollHeight;
                    });
                    if (this.aiAnswersPendingAnswer) await this.aiAnswersRefresh();
                } else await this.aiAnswersRefresh();
                if (this.aiAnswersDrawerOpen) await this.aiAnswersLoadList();
            } catch (error) {
                this.aiAnswersError = error.message;
            } finally {
                this.aiAnswersBusy = false;
            }
        },
        async aiAnswersFeedback(message, feedback) {
            try {
                await this.aiAnswersRequest(this.aiAnswersBoot.feedbackBaseUrl + '/' + message.id + '/feedback', {
                    method: 'POST', body: JSON.stringify({ feedback }),
                });
                message.feedback = feedback;
            } catch (error) { this.aiAnswersError = error.message; }
        },
        async aiAnswersLoadReview(before = null) {
            if (!this.aiAnswersBoot?.reviewUrl || this.aiAnswersWorking) return;
            this.aiAnswersStopPolling();
            const version = ++this.aiAnswersRequestVersion;
            this.aiAnswersLoading = true;
            this.aiAnswersError = '';
            try {
                const body = await this.aiAnswersRequest(this.aiAnswersBoot.reviewUrl + (before ? '?before=' + encodeURIComponent(before) : ''));
                if (version !== this.aiAnswersRequestVersion) return;
                this.aiAnswersReviewRows = before ? this.aiAnswersReviewRows.concat(body.conversations || []) : (body.conversations || []);
                this.aiAnswersReviewNextBefore = body.next_before;
                this.aiAnswersReviewMode = true;
                this.aiAnswersShowConversations = true;
                this.aiAnswersConversationId = null;
                this.aiAnswersMessages = [];
            } catch (error) {
                if (version === this.aiAnswersRequestVersion) this.aiAnswersError = error.message;
            } finally {
                if (version === this.aiAnswersRequestVersion) this.aiAnswersLoading = false;
            }
        },
        async aiAnswersOpenReview(id) {
            const version = ++this.aiAnswersRequestVersion;
            this.aiAnswersLoading = true;
            try {
                const body = await this.aiAnswersRequest(this.aiAnswersBoot.reviewUrl + '/' + encodeURIComponent(id));
                if (version !== this.aiAnswersRequestVersion) return;
                this.aiAnswersConversationId = id;
                this.aiAnswersMessages = body.messages || [];
                this.aiAnswersReviewMode = true;
                this.aiAnswersShowConversations = false;
            } catch (error) {
                if (version === this.aiAnswersRequestVersion) this.aiAnswersError = error.message;
            } finally {
                if (version === this.aiAnswersRequestVersion) this.aiAnswersLoading = false;
            }
        },
    },
};
